import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form990TShcA = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 990-T Schedule A Filing Guide & Instructions"
          description="Learn how to complete Form 990-T Schedule A for tax-exempt organizations. Get step-by-step instructions with TaxZerone to file correctly and on time."
          keywords="Form 990-EZ Schedule A, Public charity support, Charitable contributions, Tax-exempt organizations, 501(c)(3) organizations"
          canonical="resources/tax-exempt/form-990-t-schedule-a/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 990-T{' '}
          <span className="highlight-container">
            <span className="highlight">Schedule A</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Easily prepare and file Schedule A of Form 990-T to report UBI
          allocations with TaxZerone!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=f990tscha`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-990-n-vs-form-990-ez/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 990-T Schedule A Instructions
                  </Typography>

                  <Box
                    component="ul"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <strong style={{ fontWeight: 600 }}>
                        Purpose: Form 990-T Schedule A{' '}
                      </strong>{' '}
                      is used to report the income, deductions, and taxable
                      income for{' '}
                      <strong style={{ fontWeight: 600 }}>
                        each separate unrelated trade or business
                      </strong>{' '}
                      conducted by a tax-exempt organization.
                    </li>

                    <li>
                      <strong style={{ fontWeight: 600 }}>
                        Who Must File?
                      </strong>{' '}
                      Organizations required to{' '}
                      <Link
                        href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-t/"
                        sx={{
                          color: '#0202ff',
                          textDecoration: 'underline',
                        }}
                      >
                        file Form 990-T
                      </Link>{' '}
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        with $1,000 or more of gross unrelated business income
                        (UBI){' '}
                      </strong>
                      must complete
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        Schedule A
                      </strong>{' '}
                      for each unrelated trade or business.
                    </li>
                    <li>
                      <strong style={{ fontWeight: 600 }}>
                        Separate Schedule Requirement:
                      </strong>{' '}
                      If an organization operates
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        multiple unrelated trades or businesses,
                      </strong>{' '}
                      it must prepare and attach a{' '}
                      <strong style={{ fontWeight: 600 }}>
                        separate Schedule A
                      </strong>{' '}
                      for each activity.
                    </li>

                    <li>
                      <Link
                        href="https://www.taxzerone.com/resources/tax-exempt/form-990-due-dates/"
                        sx={{
                          color: '#0202ff',
                          textDecoration: 'underline',
                        }}
                      >
                        Due Date:
                      </Link>{' '}
                      Schedule A is filed with{' '}
                      <strong style={{ fontWeight: 600 }}>Form 990-T</strong>{' '}
                      and follows the same filing deadline, generally the{' '}
                      <strong style={{ fontWeight: 600 }}>
                        15th day of the 5th month{' '}
                      </strong>{' '}
                      after the end of the tax year.{' '}
                      <strong style={{ fontWeight: 600 }}>
                        Employee trusts
                      </strong>{' '}
                      generally file by the{' '}
                      <strong style={{ fontWeight: 600 }}>
                        15th day of the 4th month.
                      </strong>
                    </li>
                  </Box>
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                {/* <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Introduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Tax-exempt organizations with unrelated business income (UBI)
                  must file{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-t/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 990-T
                    </Link>
                  </span>
                  , <i>Exempt Organization Business Income Tax Return</i>. This
                  form helps the IRS understand and tax income earned from
                  activities outside the organization&apos;s charitable mission
                  and calculate the tax owed on that business income.
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  <span style={{ fontWeight: 600 }}>Form 990-T Schedule A</span>{' '}
                  is a supplementary document to report the details of each
                  unrelated trade or business. Let’s learn more about Schedule A
                  and its purpose in this resource article.
                </Typography> */}
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Form 990-T Schedule A?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file 990-T Schedule A?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#when" sx={{ color: '#0202ff' }}>
                          When is Form 990-T Schedule A Due?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#use" sx={{ color: '#0202ff' }}>
                          Use TaxZerone to complete your Form 990-T Schedule A
                          filing.
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What is Form 990-T Schedule A?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Form 990-T Schedule A</span>{' '}
                  is a supplementary schedule that breaks down unrelated
                  business income (UBI) for each separate trade or business
                  conducted by a{' '}
                  <Link href="/tax-exempt/" sx={{ color: '#0202ff' }}>
                    tax-exempt organization
                  </Link>
                  .
                </Typography>

                <Typography variant="body1" sx={{ mb: 5 }}>
                  It provides a detailed breakdown of income, expenses,
                  deductions, and taxable income for each unrelated activity,
                  allowing for an accurate calculation of the total tax owed on
                  Unrelated business income.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who must file 990-T Schedule A?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Any tax-exempt organization that has{' '}
                  <span style={{ fontWeight: 600 }}>
                    {/* <Link
                      href="/tax-exempt/e-file-irs-form-990/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 990
                    </Link>{' '} */}
                    unrelated business income from one or more unrelated trades
                    or businesses
                  </span>{' '}
                  must file{' '}
                  <span style={{ fontWeight: 600 }}>Form 990-T Schedule A</span>
                  . Unrelated business activities are defined as those that are
                  not substantially related to the organization&apos;s exempt
                  purpose.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  This includes various types of tax-exempt entities such as:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>Charitable organizations</li>
                  <li style={{ marginBottom: 16 }}>Religious organizations</li>
                  <li style={{ marginBottom: 16 }}>Educational institutions</li>
                  <li>Other nonprofit entities</li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If an organization engages in multiple unrelated business
                  activities, it must complete a separate Schedule A for each
                  business.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="when"
                >
                  When is Form 990-T Schedule A Due?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The{' '}
                  <Link
                    href="/resources/tax-exempt/form-990-due-dates/"
                    sx={{ color: '#0202ff' }}
                  >
                    deadline
                  </Link>{' '}
                  to file{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-t/"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS Form 990-T
                  </Link>{' '}
                  is the{' '}
                  <span style={{ fontWeight: 600 }}>
                    15th day of the 5th month
                  </span>{' '}
                  after the organization&apos;s accounting period ends. If your
                  organization follows the calendar tax year, the deadline for
                  filing the 990-T return is{' '}
                  <span style={{ fontWeight: 600 }}>May 15</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  An employee&apos;s trust defined in section 401(a), an IRA
                  (including SEPs and SIMPLEs), a Roth IRA, a Coverdell ESA, or
                  an Archer MSA must file Form 990-T by the{' '}
                  <span style={{ fontWeight: 600 }}>
                    15th day of the 4th month
                  </span>{' '}
                  after the end of its tax year.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> The
                  deadline is extended to the next business day if the due date
                  falls on a weekend or a legal holiday.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="use"
                >
                  Use TaxZerone to complete your Form 990-T Schedule A filing
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-990-t-schedule-a-img.webp" />
                  <source srcSet="/assets/images/form-990-t-schedule-a-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-990-t-schedule-a-img.png"
                    loading="lazy"
                    alt="The following information is required to complete and file IRS Form 990-T Schedule A"
                    width={749}
                    height={214}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Item A
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter here the same name as entered in the heading area of
                  Form 990-T.{' '}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Item B
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter in the line the same EIN as entered in item D of Form
                  990-T.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Item C
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  For each Schedule A, enter a 6-digit business activity code
                  that best describes the unrelated trade or business. If using
                  a 2-digit NAICS code, add four zeros after it. For example,
                  for retail trade (code 45), enter “450000”.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Item E -</span> Describe
                  the unrelated trade or business conducted by the organization.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part I - Unrelated Trade or Business Income
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990t-sch-a-part1-img.webp" />
                  <source srcSet="/assets/images/990t-sch-a-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990t-sch-a-part1-img.png"
                    loading="lazy"
                    alt="Schedule A (Form 990-T) Part I lines 1–13 unrelated trade or business income"
                    width={720}
                    height={370}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the gross receipts from an unrelated trade or business
                  regularly conducted that involves the sale of goods or
                  performance of services.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 1b -</span> Enter the
                  less returns and allowances on this line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 2 -</span> Enter the
                  cost of goods sold (Part III, line 8) on this line.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 2 from line 1c and enter the difference value
                  here
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 4a </span> <br /> Most
                  organizations filing Form 990-T don’t pay tax on capital gains
                  from selling property. However, tax applies to:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    Gains from{' '}
                    <span style={{ fontWeight: 600 }}>
                      debt-financed property
                    </span>{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Timber sales</span>{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Ordinary gains</span> from
                    certain property (like Sections 1245, 1250, etc.){' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    S corporation gains/losses
                  </span>{' '}
                  are taxable and must be reported on{' '}
                  <span style={{ fontWeight: 600 }}>
                    Form 990-T, Part I, line 4.
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Capital gains/losses must also be reported using:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    <Link
                      href="/resources/tax-exempt/schedule-d-form-1041-resource-guide/"
                      sx={{ color: '#0202ff' }}
                    >
                      Schedule D (Form 1041)
                    </Link>{' '}
                    for trusts{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Timber sales</span>{' '}
                  </li>
                  <li>
                    <Link
                      href="/resources/tax-exempt/schedule-d-form-1120-guide/"
                      sx={{ color: '#0202ff' }}
                    >
                      Schedule D (Form 1120){' '}
                    </Link>
                    and{' '}
                    <Link
                      href="/resources/tax-exempt/form-8949-sales-and-dispositions-guide/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 8949
                    </Link>{' '}
                    for corporations{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  These forms must be{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-t/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    attached to Form 990-T
                  </Link>{' '}
                  when filing.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 4b </span> <br />{' '}
                  Report gains or losses from non-capital assets on Form 4797.
                  Enter the net gain or loss from Part II, line 17.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.2 }}>
                  If the exempt organization is reporting ordinary gains on
                  certain types of property (like sections 1245, 1250, etc.),
                  only include depreciation or similar deductions that were used
                  to calculate taxable income during the period the organization
                  was not tax-exempt.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 4c </span>
                  <br /> If a trust has a net capital loss, it is subject to the
                  limitations of Schedule D (Form 1041). Enter on this line the
                  loss figured on Schedule D (Form 1041).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Some partnerships allow income and related deductions to be
                  grouped together, as explained in Regulations section
                  1.512(a)-6.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.2 }}>
                  Also, trusts and certain corporations may face limits on how
                  much income or loss they can claim from partnerships or S
                  corporations due to passive activity loss (PAL) rules (section
                  469) and at-risk rules (section 465). For more details, check
                  the explanation under Part I of the instructions on Unrelated
                  Trade or Business Income.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount computed on Part IV, line 3, on Part I, line
                  6, column (A) <br />
                  Enter the amount computed on Part IV, line 5, on Part I, line
                  6, column (B).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount computed on Part V, line 8, on Part I, line
                  7, column (A)
                  <br /> Enter the amount computed on Part V, line 10, on Part
                  I, line 7, column (B).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the sum of columns 5 and 10 from Part VI on Part I, line
                  8, column (A)
                  <br /> Enter the sum of columns 6 and 11 from Part VI on Part
                  I, line 8, column (B).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the sum of amounts from Part VII, column 2, on Part I,
                  line 9, column (A).
                  <br />
                  Enter the sum of amounts in Part VII, column 5, on Part I,
                  line 9, column (B).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 10
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount computed on Part VIII, line 2, on Part I,
                  line 10, column (A).
                  <br /> Enter the amount computed on Part VIII, line 3, on Part
                  I, line 10, column (B).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount computed on Part IX, line 2, on Part I, line
                  11, column (A). Enter the amount computed on Part IX, line 3,
                  on Part I, line 11, column (B).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 12
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.2 }}>
                  Enter on Part I, line 12, any item of unrelated business
                  income from a particular trade or business that isn&apos;t
                  reportable elsewhere on the return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.2 }}>
                  Attach a statement describing the sources of the other income
                  and their amounts. Such amounts may include:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    Recoveries of bad debts deducted in earlier years under the
                    specific charge-off method;{' '}
                  </li>
                  <li>
                    The amount from Form 6478, Biofuel Producer Credit (if
                    applicable);{' '}
                  </li>
                  <li>
                    The amount from Form 8864, Biodiesel, Renewable Diesel, or
                    Sustainable Aviation Fuels Credit (if applicable); and{' '}
                  </li>
                  <li>
                    Proceeds received from employer-owned life insurance
                    contracts issued after August 17, 2006 (complete and attach
                    Form 8925).{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 13
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use the amount from Schedule A, Part I, line 13, column (C),
                  in the computation of UBTI in Part II.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part II - Deductions Not Taken Elsewhere
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990t-sch-a-part2-img.webp" />
                  <source srcSet="/assets/images/990t-sch-a-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990t-sch-a-part2-img.png"
                    loading="lazy"
                    alt="Schedule A for Form 990-T Part II lines 1–18 showing deductions directly connected with unrelated business income."
                    width={750}
                    height={347}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 1 </span> <br /> Enter
                  the details of the compensation provided to officers,
                  directors, and trustees.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 2 </span> <br /> Enter
                  here the salaries and wages provided for the tax year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the cost of incidental repairs and maintenance not
                  claimed elsewhere on the return, such as labor and supplies,
                  that don&apos;t add to the value or appreciably prolong the
                  life of the property.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total receivables from an unrelated trade or
                  business that were previously included in taxable income and
                  that became worthless in whole or in part during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Attach a separate statement listing the interest being claimed
                  on this line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Interest allocation -
                  </span>{' '}
                  If the proceeds of a loan were used for more than one purpose
                  (for example, to purchase a portfolio investment and to
                  acquire an interest in a passive activity), an interest
                  allocation must be made. See Temporary Regulations section
                  1.163-8T for the interest allocation rules.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Tax-exempt interest -
                  </span>{' '}
                  Don&apos;t include interest on indebtedness incurred or
                  continued to purchase or carry obligations on which the
                  interest income is totally exempt from income tax. For
                  exceptions, see section 265(b).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Prepaid interest -</span>{' '}
                  Generally, a cash basis taxpayer can’t deduct prepaid interest
                  allocable to years following the current tax year, for
                  example, during the tax year a cash basis taxpayer prepaid
                  interest on a loan. The taxpayer can deduct only that part of
                  the prepaid interest that was for the use of the loaned funds
                  during the tax year, not for the use of the loaned funds
                  during the subsequent years.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Straddle interest -</span>{' '}
                  Generally, the interest and carrying charges on straddles
                  can’t be deducted and must be capitalized. See section 263(g).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Original issue discount -
                  </span>{' '}
                  See section 163(e)(5) for special rules for the disqualified
                  portion of original issue discount on a high-yield discount
                  obligation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Interest on certain underpayments of tax -
                  </span>{' '}
                  Don&apos;t deduct interest paid or incurred on any portion of
                  an underpayment of tax that is attributable to an
                  understatement arising from an undisclosed listed transaction
                  or an undisclosed reportable avoidance transaction (other than
                  a listed transaction) entered into in tax years beginning
                  after October 22, 2004.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Interest allocable to the production of designated property
                    -
                  </span>{' '}
                  Don&apos;t deduct interest on debt allocable to the production
                  of designated property. Interest that is allocable to such
                  property produced by an organization for its own use or for
                  sale must be capitalized. An organization must also capitalize
                  any interest on debt allocable to an asset used to produce the
                  earlier property. See{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    section 263A(f) and Regulations sections 1.263A-8 through
                    1.263A-15.
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Interest on below-market loans -
                  </span>{' '}
                  See section 7872 for special rules regarding the deductibility
                  of foregone interest on certain below-market-rate loans.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter taxes and license fees paid or accrued during the year,
                  but don&apos;t include the following taxes.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>Federal income taxes. </li>
                  <li>
                    Foreign or U.S. territory income taxes if a foreign tax
                    credit is claimed.{' '}
                  </li>
                  <li>Taxes not imposed on your organization. </li>
                  <li>
                    Taxes, including state or local sales taxes, paid or
                    incurred in connection with an acquisition or disposition of
                    property. These taxes must be treated as part of the cost of
                    the acquired property or, in the case of a disposition, as a
                    reduction in the amount realized on the disposition.{' '}
                  </li>
                  <li>
                    Taxes assessed against local benefits that increase the
                    value of the property assessed (such as for paving,
                    etc.).{' '}
                  </li>
                  <li>
                    Taxes deducted elsewhere on the return, such as those
                    reflected in cost of goods sold.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Besides regular depreciation, include on line 7 any Section
                  179 expenses for certain property the organization placed in
                  service this year or carried over from last year. See Form
                  4562 for details.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 8 </span> <br /> Enter
                  here the less depreciation claimed in Part III and elsewhere
                  on return.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check sections 613 and 613A for percentage depletion rates for
                  natural deposits. Attach Form T (Timber), Forest Activities
                  Schedules, if a deduction is taken for depletion of timber.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 10
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Employers who maintain pension, profit-sharing, or other
                  funded deferred compensation plans are generally required to
                  file Form 5500. This requirement applies whether or not the
                  plan is qualified under the Code and whether or not a
                  deduction is claimed for the current tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Section 6652(e) imposes a penalty for late filing of these
                  forms. In addition, there is a penalty for overstating the
                  pension plan deduction. See section 6662(f).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount of contributions to employee benefit programs
                  (such as insurance, health, and welfare programs) that
                  aren&apos;t an incidental part of a deferred compensation plan
                  included on Schedule A, Part II, line 10.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 12
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount computed on Part VIII, line 7 (if
                  applicable), on Part II, line 12.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 13
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount computed on Part IX, line 8a (if applicable),
                  on Part II, line 13.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 14
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter on this line the deduction taken for amortization (see
                  Form 4562) as well as other authorized deductions for which no
                  space is provided on the return. Attach a statement listing
                  the deductions claimed on this line. On each Schedule A,
                  deduct only items directly connected with the unrelated trade
                  or business for which income is reported on that Schedule A.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Extraterritorial income exclusion.
                  </span>{' '}
                  Complete Form 8873 and include the deduction from line 52 in
                  other deductions reported on Part II, line 14.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Don&apos;t deduct fines or penalties paid to a government for
                  violating any law. The exclusion was repealed generally for
                  transactions after 2004, with some exceptions. See Form 8873
                  and its instructions.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 15
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add lines 1 through 14 and enter the sum value in this line.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 16
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 15 from Part I, line 13, column (C) and enter
                  the difference in this line.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 17
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If your organization has a Net Operating Loss (NOL) carryover
                  from a previous year for a specific unrelated business, enter
                  it on Schedule A, Part II, line 17—but only up to the amount
                  of income reported on line 16 for that business.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Even if you weren’t required to{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-t/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 990-T
                  </Link>{' '}
                  in earlier years, you can still claim the carryover—just
                  include a statement showing how it was calculated. If that
                  business was closed, sold, or otherwise ended, any remaining
                  NOL is suspended but may be used later if the same business
                  resumes or a new one with the same NAICS code starts.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 18
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 17 from line 16 and enter the difference in this
                  line.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part III - Cost of Goods Sold
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990t-sch-a-part3-img.webp" />
                  <source srcSet="/assets/images/990t-sch-a-part3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990t-sch-a-part3-img.png"
                    loading="lazy"
                    alt="Form 990-T Part III lines 1–9 cost of goods sold"
                    width={720}
                    height={163}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the organization changes its accounting method to stop
                  tracking inventories, it must recalculate last year’s closing
                  inventory using the new method and enter that on line 1. If
                  the new amount is different, explain why and include the
                  difference in the section 481(a) adjustment.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the Purchases value in this line.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter in this line the cost of labor made by the organization
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the total of these additional costs on Schedule A, Part
                  III, line 4, but only if they’re not already included on lines
                  2 or 3.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Only fill out this line if your organization uses the
                  simplified accounting method.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    If you use the simplified production method, report costs
                    that now need to be capitalized under section 263A but
                    weren’t before.{' '}
                  </li>
                  <li>
                    If you use the simplified resale method, report costs
                    related to{' '}
                  </li>
                  <ul>
                    <li>Off-site storage </li>
                    <li>Purchasing</li>
                    <li>Handling (like repackaging or transporting) </li>
                    <li>Certain admin costs (mixed service costs) </li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter here any costs paid or incurred during the tax year not
                  entered on Schedule A, Part III, lines 2 through 4. Attach a
                  statement describing the other costs.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 6 </span> <br /> Add
                  lines 1 through 5 and enter the sum value in this line.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If your organization treats inventory like non-incidental
                  materials and supplies, report here the cost of raw materials
                  and merchandise bought for resale (from line 6) that weren’t
                  sold during the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Also, for calculating extra costs under section 263A that
                  should be included in ending inventory, refer to Regulations
                  sections 1.263A-1 through 1.263A-3.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 7 from line 6 and enter the difference value
                  here and in Part I, line 2.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 9 </span> <br />{' '}
                  Select “Yes” or “No” whether the rules of section 263A (with
                  respect to property produced or acquired for resale) apply to
                  the organization?
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part IV - Rent Income (From Real Property and Personal
                  Property Leased With Real Property)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990t-scha-part4-line1-5-img.webp" />
                  <source srcSet="/assets/images/f990t-scha-part4-line1-5-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990t-scha-part4-line1-5-img.png"
                    loading="lazy"
                    alt="Part IV tax form section displaying rent income entries and deductions from Lines 1–5"
                    width={750}
                    height={358}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  All organizations that have applicable rent income, other than
                  section 501(c)(7), (9), and (17) organizations, should
                  complete Schedule A, Part IV.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For organizations other than section 501(c)(7), (9), and (17)
                  organizations, only the following rents are taxable on
                  Schedule A, Part I, line 6.
                </Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Rents from personal property leased with real property, if
                    the rents from the personal property are more than 10% of
                    the total rents received or accrued under the lease,
                    determined at the time the personal property is placed in
                    service.
                  </li>{' '}
                  <li>Rents from real and personal property if:</li>{' '}
                  <ul style={{ listStyleType: 'lower-alpha' }}>
                    <li>
                      More than 50% of the total rents received or accrued under
                      the lease are for personal property, or{' '}
                    </li>
                    <li>
                      The amount of the rent depends on the income or profits
                      derived by any person from the property leased (except an
                      amount based on a fixed percentage of receipts or sales).
                    </li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 1 </span> <br /> Check
                  the box next to the property description if the property is
                  used both to carry on exempt activities and to conduct
                  unrelated trade or business activities. Provide description of
                  property (property street address, city, state, ZIP code).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 2a </span>
                  <br /> Enter the rent received or accrued from personal
                  property (if the percentage of rent for personal property is
                  more than 10% but not more than 50%)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 2b </span>
                  <br /> Enter the rent received or accrued from real and
                  personal property (if the percentage of rent for personal
                  property exceeds 50% or if the rent is based on profit or
                  income)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 2c </span>
                  <br /> Add total rents received or accrued by property. Add
                  lines 2a and 2b, columns A through D .
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 3 </span>
                  <br /> Enter here the total rents received or accrued. Add
                  line 2c, columns A through D. Enter the sum value here and on
                  Part I, line 6, column (A)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 4 </span>
                  <br /> For each property, attach a statement describing the
                  directly connected deductions and their amounts.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add line 4, columns A through D and enter the sum value here
                  and on Part I, line 6, column (B).
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part V - Unrelated Debt-Financed Income
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990t-sch-a-part5-img.webp" />
                  <source srcSet="/assets/images/990t-sch-a-part5-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990t-sch-a-part5-img.png"
                    loading="lazy"
                    alt="Form 990-T Part V lines 1–11 unrelated debt-financed income"
                    width={720}
                    height={447}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Line 1 </span> <br /> Enter
                  the address of the debt-financed property. If the
                  debt-financed property isn’t real property, enter the address
                  where the property is located and describe the property in
                  Part XI, Supplemental Information.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check the box next to the property description if the property
                  is used both to carry on exempt activities and to conduct
                  unrelated trade or business activities.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 2 </span>
                  <br /> Enter the gross income from debt financed property,
                  excluding income otherwise included in UBTI. For example,
                  don&apos;t include rents from personal property shown on
                  Schedule A, Part IV, or rents and interest from controlled
                  organizations shown on Schedule A, Part VI.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 3 </span>
                  <br /> Use this line to report expenses for property you
                  bought with a loan that’s used to earn income subject to
                  unrelated business tax.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 3a</span> <br />
                  Write the yearly portion of the property’s cost you’re
                  deducting. This simply spreads the property’s cost evenly over
                  the years you expect to use it. Attach a note showing your
                  calculation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 3b</span> <br />
                  Include other costs linked to the property, like loan
                  interest, insurance, or repairs. Attach details, so it’s clear
                  how you figured the amount.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 3c</span> <br />
                  Add up 3a and 3b for each column to get the total.
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  To calculate the average acquisition debt for a property
                  during the tax year:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    Add up the amount of outstanding principal debt on the first
                    day of each month the organization owned the property.{' '}
                  </li>
                  <li>
                    Divide the total by the number of months the property was
                    held.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Then:
                </Typography>
                <Box component="ol" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    Use this average to figure out how much of the property’s
                    income is linked to the debt (debt-financed income %).{' '}
                  </li>
                  <li>
                    Multiply the average debt by that percentage to get the
                    portion subject to tax.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Line 5 </span>
                  <br /> To calculate the average adjusted basis of
                  debt-financed property, take the property&apos;s adjusted
                  value on the first and last day you held it during the tax
                  year, and average them. The adjusted basis comes from Section
                  1011 and must be reduced by all depreciation taken in prior
                  years—even if your organization was tax-exempt then. If your
                  organization was taxed on UBTI in certain years, you still
                  reduce the basis by all allowable depreciation, not just the
                  portion used to calculate UBTI.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Attach a statement showing, for each property:
                </Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>A brief description of the property, </li>{' '}
                  <li>The adjusted basis, </li>{' '}
                  <li>The percent allocable to debt-financed income, and </li>{' '}
                  <li>The product of (3) multiplied by (4). </li>{' '}
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If no adjustments to the basis of property under section 1011
                  apply, the basis of the property is cost.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 6 </span>
                  <br /> Divide each property&apos;s average acquisition
                  indebtedness for the tax year by that property&apos;s average
                  adjusted basis during the period it is held in the tax year.
                  This percentage cannot be more than 100%.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 7 </span>
                  <br /> The amount of income from debt-financed property
                  included in unrelated trade or business income is figured by
                  multiplying the property&apos;s gross income by the percentage
                  computed on line 6.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 8 </span>
                  <br /> Enter on line 8 the sum of amounts computed for each
                  property on line 7. Also enter this amount on Part I, line 7,
                  column (A).
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Line 9 </span>
                  <br /> For each debt-financed property, take the total
                  connected deductions (including eligible dividend deductions)
                  and multiply by the debt percentage from line 6.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If the property is depreciable, use only straight-line
                  depreciation and show this amount on Schedule A, Part V, line
                  3a.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Attach detailed statements showing how you calculated
                  depreciation and other expenses (for lines 3a and 3b).
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you own stock that&apos;s debt-financed, use Form 1120
                  Schedule C to find the dividend deduction to report.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For capital losses carried back or forward, apply the debt
                  percentage when figuring the loss amount to carry — but don’t
                  apply it again in the year you use the loss.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 10
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  On line 10, enter the sum of amounts computed for each
                  property on line 9. Also enter this amount on Part I, line 7,
                  column (B).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total dividends-received deductions (after
                  reduction, when applicable, by the debt-basis percentage(s))
                  included on Schedule A, Part V, line 9.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part VI - Interest, Annuities, Royalties, and Rents From
                  Controlled Organizations
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990t-sch-a-part6-img.webp" />
                  <source srcSet="/assets/images/990t-sch-a-part6-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990t-sch-a-part6-img.png"
                    loading="lazy"
                    alt="Form 990-T Part VI interest, annuities, royalties, and rents from controlled organizations"
                    width={760}
                    height={380}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Under Section 512(b)(13), if a controlling tax-exempt
                  organization gets interest, rent, royalties, or annuities from
                  a controlled organization, those amounts may be taxable, even
                  if the controlling organization isn’t actively running a
                  business.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  However, there are exceptions—
                  <span style={{ fontWeight: 600 }}>
                    some amounts might be taxed differently under other tax
                    rules{' '}
                  </span>
                  (see Reg. 1.512(b)-1(l)(5)).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Column 1 -</span> Enter the
                  name of controlled organization
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Column 2 -</span> Enter the
                  organisation’s Employer identification number
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Column 3 -</span> Enter the
                  net unrelated income (or net unrelated loss) of each
                  controlled entity listed that is exempt from tax under section
                  501(a).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Column 7 -</span> Enter the
                  taxable income of each nonexempt controlled organization.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Column 8 -</span> Report
                  the income or loss from each related business that would be
                  taxed like UBTI if it were a nonprofit.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column 4 or 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For each controlled organization, list the total payments
                  received.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you got both{' '}
                  <span style={{ fontWeight: 600 }}>
                    regular specified payments
                  </span>{' '}
                  and{' '}
                  <span style={{ fontWeight: 600 }}>
                    qualifying specified payments
                  </span>{' '}
                  from the same organization,{' '}
                  <span style={{ fontWeight: 600 }}>enter them separately</span>{' '}
                  on two lines.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Column 5 or 10 -</span> For
                  specified payments, enter the portion of column 4 or 9 to the
                  extent that the payment reduced the net unrelated income (or
                  increased the net unrelated loss) of the controlled entity.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  <span style={{ fontWeight: 600 }}> Column 6 or 11 -</span>
                  Enter only the expenses that are directly related to the
                  income shown in column 5 or 10.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For qualifying specified payments, only include expenses
                  related to the portion of the payment that exceeds its fair
                  market value (FMV). Don&apos;t include expenses for the part
                  of the payment that&apos;s not counted as income.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part VII - Investment Income of a Section 501(c)(7), (9), or
                  (17) Organization
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990t-sch-a-part7-img.webp" />
                  <source srcSet="/assets/images/990t-sch-a-part7-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990t-sch-a-part7-img.png"
                    loading="lazy"
                    alt="Form 990-T Part VII Investment Income of a Section 501(c)(7), (9), or (17) Organization"
                    width={720}
                    height={198}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For 501(c)(7), (9), or (17) organizations:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    Report all investment income from non-members on Schedule A,
                    Part VII — including income from securities and
                    debt-financed property (with related expenses).{' '}
                  </li>
                  <li>Don’t report this non-member income on Part V. </li>
                  <li>
                    Exclude interest from state/local government bonds (section
                    103(a)).{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If a 501(c)(7), (9), or (17) organization sells property used
                  for its exempt purpose and buys new exempt-use property within
                  1 year before or 3 years after the sale, it only pays tax on
                  the gain if it makes more money from the sale than it spent on
                  the new property. The new property doesn&apos;t have to be the
                  same type. The IRS must be notified by attaching a statement
                  to the tax return. For figuring out the gain, follow the
                  instructions for Part V, line 5.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Column 1 -</span> Enter
                  here the description of income
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Column 2 -</span> Enter
                  here the amount of income
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Column 3 -</span> Only
                  deduct expenses that are directly related to net investment
                  income. If needed, split expenses between exempt and other
                  activities. Dividends-received deductions can’t be used since
                  they don’t directly relate to earning income.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Column 4 -</span> Section
                  501(c)(7), (9), and (17) organizations may set aside income
                  that would otherwise be taxable under section 512(a)(3).
                  However, income derived from an unrelated trade or business
                  may not be set aside and thus can’t be exempt function income.
                  In addition, any income set aside and later used for other
                  purposes must be included in income.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Report income set aside on Schedule A, Part VII, column 4.
                  Attach a statement listing:
                </Typography>
                <Box component="ol" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>The amount set aside for charitable purposes; </li>
                  <li>
                    The amount set aside for reasonable administration costs
                    directly connected with such amount;{' '}
                  </li>
                  <li>
                    The amount set aside for payment of life, sickness,
                    accident, or other benefit; and{' '}
                  </li>
                  <li>
                    The amount set aside for reasonable administration costs
                    directly connected with the payment of such benefits.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Amounts set aside aren&apos;t deductible under section 170 or
                  any other section of the Code.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add amounts in column 3 and 4 and enter here.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part VIII - Exploited Exempt Activity Income, Other Than
                  Advertising Income
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990t-sch-a-part8-img.webp" />
                  <source srcSet="/assets/images/990t-sch-a-part8-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990t-sch-a-part8-img.png"
                    loading="lazy"
                    alt="Part VIII Line 1-7 Exploited Exempt Activity Income, Other Than Advertising Income"
                    width={720}
                    height={179}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If an exempt organization earns money from a business activity
                  that takes advantage of its exempt purpose (but isn’t from
                  periodical ads), it should report it on Schedule A, Part VIII.
                  Advertising income that’s not from a periodical also goes in
                  Part VIII.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Briefly describe here the exempt activity being exploited in
                  an unrelated trade or business activity.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 2 -</span> Enter the
                  gross unrelated business income from trade or business here
                  and on Part I, line 10, column (A)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 3 -</span> Enter the
                  expenses directly connected with production of unrelated
                  business income here and on Part I, line 10, column (B)
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 3 from line 2 and enter the difference amount
                  here. If the value is a gain, complete lines 5 through 7.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  To calculate unrelated business taxable income (UBTI):
                </Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Start with gross unrelated business income.</li>{' '}
                  <li>Subtract directly connected deductions. </li>{' '}
                  <li>
                    If there&apos;s still income left, you can deduct additional
                    expenses related to the exempt activity, but with
                    rules:{' '}
                  </li>{' '}
                  <ul style={{ listStyleType: 'lower-alpha' }}>
                    <li>
                      Only deduct the part of expenses that’s more than the
                      income from that exempt activity.{' '}
                    </li>
                    <li>
                      Limit this extra deduction to the net income from that
                      specific business activity.{' '}
                    </li>
                    <li>
                      Don’t include income/expenses from the exempt activity
                      when calculating carryovers or income from unrelated
                      activities that don’t exploit that exempt purpose.{' '}
                    </li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 5 -</span> Enter the
                  gross income from activity that is not unrelated business
                  income.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 6 -</span> Enter the
                  expenses attributable to income entered on line 5.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 5 from line 6, but do not enter more than the
                  amount on line 4. Enter here and on Part II, line 12.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part IX - Advertising Income
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990t-sch-a-part9-img.webp" />
                  <source srcSet="/assets/images/990t-sch-a-part9-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990t-sch-a-part9-img.png"
                    loading="lazy"
                    alt="Part IX Line 1-8a Exploited Exempt Activity Income, Other Than Advertising Income"
                    width={720}
                    height={468}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  An exempt organization that earned gross income from the sale
                  of advertising in an exempt organization periodical must
                  complete Schedule A, Part IX.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 1 </span>
                  <br /> Enter here the Name(s) of periodical(s). Check box if
                  reporting two or more periodicals on a consolidated basis.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 2a </span>
                  <br /> Add columns A through D, and enter the sum value here
                  and on Part I, line 11, column (A)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 3a </span>
                  <br /> Add columns A through D, and enter here and on Part I,
                  line 11, column (B)
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 3 from line 2. For any column in line 4 showing
                  a gain, complete lines 5 through 8. For any column in line 4
                  showing a loss or zero, do not complete lines 5 through 7, and
                  enter -0- on line 8.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 5 </span>
                  <br /> Enter the readership costs in this line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 6 </span>
                  <br /> Enter the circulation income in this line.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If line 6 is less than line 5, subtract line 6 from line 5. If
                  line 5 is less than line 6, enter -0-
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For each column showing a gain on line 4, enter the lesser of
                  line 4 or line 7 here.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 8a </span>
                  <br /> Add line 8, columns A through D. Enter the greater of
                  the line 8a columns total or -0- here and on Part II, line 13.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part X - Compensation of Officers, Directors, and Trustees
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990t-sch-a-part10-img.webp" />
                  <source srcSet="/assets/images/990t-sch-a-part10-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990t-sch-a-part10-img.png"
                    loading="lazy"
                    alt="Part X Compensation of Officers, Directors, and Trustees"
                    width={720}
                    height={162}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Complete columns 1 through 4 for those officers, directors,
                  and trustees whose salaries or other compensation are
                  allocable to unrelated business gross income.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  However, don&apos;t include in column 4 compensation that is
                  deducted on Schedule A, Part II, lines 2 and 14, or any line
                  of Schedule A, Parts III through IX.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part XI - Supplemental Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990t-sch-a-part11-img.webp" />
                  <source srcSet="/assets/images/990t-sch-a-part11-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990t-sch-a-part11-img.png"
                    loading="lazy"
                    alt="Part XI Supplemental Information"
                    width={720}
                    height={114}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Use Part XI to explain your organization’s activities, clarify
                  any lines on Schedule A that lack space for extra info,
                  support the amounts reported, or explain changes from prior
                  years (like switching business activity codes).
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For each entry, include:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    The{' '}
                    <span style={{ fontWeight: 600 }}>
                      Schedule A part and line number,
                    </span>
                    Schedule A part and line number,{' '}
                  </li>{' '}
                  <li>
                    A{' '}
                    <span style={{ fontWeight: 600 }}>
                      brief description,
                    </span>{' '}
                  </li>{' '}
                  <li>
                    The <span style={{ fontWeight: 600 }}>amount involved</span>{' '}
                    (if any).{' '}
                  </li>{' '}
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If necessary, you may also attach a PDF document to provide
                  supplemental information.
                </Typography>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        This content is based on the official IRS{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i990t.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          Instructions
                        </Link>{' '}
                        for Form 990-T and Schedule A, used to report unrelated
                        business taxable income (UBTI) for tax-exempt
                        organizations.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 83,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. Is there a minimum amount of UBI that requires filing Form
                  990-T?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Yes, if an exempt organization has $1,000 or more of gross
                    income from unrelated business activities, it must file Form
                    990-T.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. What if my organization has minimal UBI?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography sx={{ mb: 2 }}>
                      Organizations with gross income of less than $1,000 from
                      unrelated business activities generally do not need to
                      file Form 990-T.
                    </Typography>
                    <Typography>
                      However, if the organization has even a small amount of
                      UBI from multiple unrelated businesses, they may still
                      need to file Form 990-T and complete separate{' '}
                      <Link
                        href="/resources/tax-exempt/form-990-schedule-a/"
                        sx={{ color: '#0202ff' }}
                      >
                        Schedule A
                      </Link>
                      &apos;s for each activity.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. What types of income are considered unrelated business
                  income (UBI)?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Unrelated Business Income generally includes income from a
                    trade or business that is regularly carried on and not
                    substantially related to the organization&apos;s exempt
                    purpose. Examples include rental income, income from sales
                    of merchandise, and fees for services.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form990TShcA;
